/**
 * Workflow Definition Modal Component
 *
 * Purpose: Modal form for creating and editing workflow definitions with validation.
 *          Handles model selection, access level configuration, and GLOBAL override handling.
 *
 * Architecture: Self-contained modal component with form validation and dependency loading.
 *              Mirrors the FieldDefinitionModal pattern from MetadataManager.
 *
 * Dependencies: React, Material-UI, WorkflowService, MetadataService
 * Author: Development Team
 * Created: 2026-04-13
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Public as GlobalIcon,
  Business as TenantIcon,
} from '@mui/icons-material';

import { WorkflowDefinition, WorkflowDefinitionFormData } from '../../../services/workflowService';
import metadataService, { Model, TenantInfo } from '../../../services/metadataService';
import { generateWorkflowId } from '../../../utils/patterns';
import DescriptionField, { descriptionLengthError } from '../../common/DescriptionField';
import { SecondaryButton, DestructiveButton } from '../../common/ActionButtons';
import DialogFooter from '../../common/DialogFooter';
import { renderMultiSelectOption } from '../../common/MultiSelectOption';

// Constants
const ACCESS_LEVELS = {
  INTERNAL_ONLY: '0',
  CLIENT_READ_ONLY: '1',
  CLIENT_OVERRIDEABLE: '2',
  CLIENT_OVERRIDEABLE_ALL: '3',
} as const;

// Component interfaces
export interface WorkflowDefinitionModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  workflowDefinition?: WorkflowDefinition;
  tenantContext: TenantInfo;
  canEdit: boolean;
  onClose: () => void;
  onSave: (data: WorkflowDefinitionFormData) => Promise<void>;
  onDelete?: (workflowDefinition: WorkflowDefinition) => Promise<void>;
}

/**
 * Modal component for create/edit workflow definition operations
 */
export const WorkflowDefinitionModal: React.FC<WorkflowDefinitionModalProps> = ({
  open,
  mode,
  workflowDefinition,
  tenantContext,
  canEdit,
  onClose,
  onSave,
  onDelete,
}) => {
  // Form data state
  const [formData, setFormData] = useState<WorkflowDefinitionFormData>({
    workflow_id: '',
    tenant_id: tenantContext.tenant_id,
    label: '',
    description: null,
    models: [],
    access: tenantContext.is_global ? ACCESS_LEVELS.CLIENT_OVERRIDEABLE : undefined,
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<Model[]>([]);
  const [loadingDependencies, setLoadingDependencies] = useState(true);

  // Load dependencies (available models)
  useEffect(() => {
    const loadDependencies = async () => {
      try {
        setLoadingDependencies(true);
        const normalModels = await metadataService.getNormalModels();
        setAvailableModels((normalModels || []).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Failed to load models:', error);
      } finally {
        setLoadingDependencies(false);
      }
    };

    if (open) {
      loadDependencies();
    }
  }, [open]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && workflowDefinition) {
        setFormData({
          workflow_id: workflowDefinition.workflow_id,
          tenant_id: workflowDefinition.tenant_id,
          label: workflowDefinition.label,
          description: workflowDefinition.description || null,
          models: workflowDefinition.models || [],
          access: workflowDefinition.access,
        });
      } else {
        setFormData({
          workflow_id: '',
          tenant_id: tenantContext.tenant_id,
          label: '',
          description: null,
          models: [],
          access: tenantContext.is_global ? ACCESS_LEVELS.CLIENT_OVERRIDEABLE : undefined,
        });
      }
      setErrors({});
      setSaveError(null);
    }
  }, [open, mode, workflowDefinition, tenantContext]);

  // Auto-generate workflow ID when label changes (create mode only)
  useEffect(() => {
    if (mode === 'create' && formData.label.trim()) {
      const id = generateWorkflowId(formData.label);
      setFormData((prev) => ({ ...prev, workflow_id: id }));
    } else if (mode === 'create' && !formData.label.trim()) {
      setFormData((prev) => ({ ...prev, workflow_id: '' }));
    }
  }, [formData.label, mode]);

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate label
    if (!formData.label.trim()) {
      newErrors.label = 'Label is required';
    } else if (formData.label.length < 2) {
      newErrors.label = 'Label must be at least 2 characters';
    } else if (formData.label.length > 100) {
      newErrors.label = 'Label must be under 100 characters';
    }

    // Validate workflow_id
    if (!formData.workflow_id.trim()) {
      newErrors.workflow_id = 'Workflow ID is required';
    } else if (!/^wf_[a-z0-9-]+$/.test(formData.workflow_id)) {
      newErrors.workflow_id =
        'Workflow ID must start with wf_ and contain only lowercase letters, numbers, and hyphens';
    }

    // Validate models (at least one required)
    if (!formData.models || formData.models.length === 0) {
      newErrors.models = 'At least one model is required';
    }

    // Validate description length (soft cap enforced at save time)
    const descError = descriptionLengthError(formData.description);
    if (descError) newErrors.description = descError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setSaveError(null);

      const dataToSave: WorkflowDefinitionFormData = {
        ...formData,
        description: formData.description?.trim() || null,
      };

      await onSave(dataToSave);
      onClose();
    } catch (error) {
      console.error('Failed to save workflow definition:', error);
      const message = error instanceof Error ? error.message : 'Failed to save workflow definition';
      setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!workflowDefinition || !onDelete) return;

    try {
      setLoading(true);
      await onDelete(workflowDefinition);
      onClose();
    } catch (error) {
      console.error('Failed to delete workflow definition:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {tenantContext.is_global ? <GlobalIcon /> : <TenantIcon />}
          {mode === 'create' ? 'Create Workflow Definition' : 'Edit Workflow Definition'}
          <Chip
            label={tenantContext.name}
            size="small"
            color={tenantContext.is_global ? 'warning' : 'primary'}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {loadingDependencies ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            {tenantContext.is_global && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are creating/editing a GLOBAL workflow definition. This will affect all tenants.
              </Alert>
            )}

            {!tenantContext.is_global &&
              workflowDefinition?.tenant_id === 'GLOBAL' &&
              mode === 'edit' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You are editing a GLOBAL workflow definition. This will create a tenant-specific
                  override instead of modifying the GLOBAL definition.
                </Alert>
              )}

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Label"
                  required
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  error={!!errors.label}
                  helperText={errors.label || 'Human-readable label for the workflow'}
                  fullWidth
                  disabled={!canEdit}
                  data-testid="workflow-label-input"
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Workflow ID"
                  value={formData.workflow_id}
                  onChange={(e) => {
                    if (mode === 'create') {
                      setFormData({ ...formData, workflow_id: e.target.value });
                    }
                  }}
                  error={!!errors.workflow_id}
                  helperText={
                    mode === 'create'
                      ? errors.workflow_id || 'Auto-generated from label (editable)'
                      : 'Workflow ID (read-only)'
                  }
                  fullWidth
                  disabled={mode === 'edit'}
                  placeholder={mode === 'create' ? 'wf_example-workflow' : undefined}
                  data-testid="workflow-id-input"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <DescriptionField
                  label="Description"
                  value={formData.description || ''}
                  onChange={(value) => setFormData({ ...formData, description: value || null })}
                  error={!!errors.description}
                  helperText={errors.description || 'Optional description of the workflow'}
                  fullWidth
                  disabled={!canEdit}
                  data-testid="workflow-description-input"
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  renderOption={renderMultiSelectOption((option: { name: string }) => option.name)}
                  options={availableModels}
                  getOptionLabel={(option) => option.name}
                  value={availableModels.filter((m) => formData.models.includes(m.model_id))}
                  onChange={(_event, newValue) => {
                    setFormData({
                      ...formData,
                      models: newValue.map((m) => m.model_id),
                    });
                  }}
                  disabled={!canEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Models"
                      required
                      error={!!errors.models}
                      helperText={
                        errors.models || 'Select one or more models this workflow applies to'
                      }
                      data-testid="workflow-models-input"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option.name}
                        size="small"
                        {...getTagProps({ index })}
                        key={option.model_id}
                      />
                    ))
                  }
                  data-testid="workflow-models-autocomplete"
                />
              </Grid>

              {tenantContext.is_global && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth disabled={!canEdit}>
                    <InputLabel>Access Level</InputLabel>
                    <Select
                      value={formData.access || ACCESS_LEVELS.CLIENT_OVERRIDEABLE}
                      onChange={(e) => setFormData({ ...formData, access: e.target.value })}
                      label="Access Level"
                      data-testid="workflow-access-select"
                    >
                      <MenuItem value={ACCESS_LEVELS.INTERNAL_ONLY}>Internal Only</MenuItem>
                      <MenuItem value={ACCESS_LEVELS.CLIENT_READ_ONLY}>Client Read-Only</MenuItem>
                      <MenuItem value={ACCESS_LEVELS.CLIENT_OVERRIDEABLE}>
                        Client Overrideable
                      </MenuItem>
                      <MenuItem value={ACCESS_LEVELS.CLIENT_OVERRIDEABLE_ALL}>
                        Client Overrideable All
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogFooter
        destructiveAction={
          mode === 'edit' &&
          onDelete &&
          canEdit && (
            <DestructiveButton
              onClick={handleDelete}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
            >
              Delete
            </DestructiveButton>
          )
        }
      >
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>

        {canEdit && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || loadingDependencies}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};

export default WorkflowDefinitionModal;
